import apiClient from '../../lib/axios';

/**
 * Get all active users (no pagination).
 * Uses GET /api/users/active - same shape as fetchUsers: { data: { users, count } }.
 */
export const fetchActiveUsers = async () => {
  const response = await apiClient.get('/api/users/active');
  return response.data;
};

export const fetchUsers = async (filters = {}) => {
  const params = new URLSearchParams();
  const normalized = { ...filters };
  if (normalized.pageSize && !normalized.limit) normalized.limit = normalized.pageSize;
  if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
  if (filters.search) params.append('search', filters.search);
  if (filters.page) params.append('page', filters.page);
  if (normalized.limit) params.append('limit', normalized.limit);
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
  if (filters.branch) params.append('branch', filters.branch);
  if (filters.gender) params.append('gender', filters.gender);
  if (filters.directorate) params.append('directorate', filters.directorate);
  if (filters.position) params.append('position', filters.position);
  const response = await apiClient.get(`/api/users?${params.toString()}`);
  return response.data;
};

function usersArrayFromUsersPayload(payload) {
  return (
    payload?.data?.users ??
    payload?.users ??
    (Array.isArray(payload?.data) ? payload.data : []) ??
    []
  );
}

function paginationFromUsersPayload(payload) {
  return (
    payload?.data?.pagination ??
    payload?.pagination ?? {
      hasNextPage: false,
    }
  );
}

/**
 * Backend-only user search: walks paginated GET /api/users until every matching row is loaded.
 * Respects server max page size (100). Intended for pickers (payee, assignee) where the full
 * active directory match set must appear, not only the first page.
 *
 * @param {{ search: string, isActive?: boolean, sortBy?: string, sortOrder?: string, branch?: string }} params
 * @param {{ pageSize?: number, maxPages?: number }} [opts]
 * @returns {Promise<object[]>}
 */
export async function fetchUsersAllPages(params = {}, opts = {}) {
  const search = String(params.search ?? '').trim();
  if (search.length < 2) return [];

  const pageSize = Math.min(100, Math.max(1, Number(opts.pageSize) || 100));
  const maxPages = Math.min(500, Math.max(1, Number(opts.maxPages) || 100));

  const base = {
    search,
    limit: pageSize,
    isActive: params.isActive !== undefined ? params.isActive : true,
    sortBy: params.sortBy || 'firstName',
    sortOrder: params.sortOrder || 'asc',
  };
  if (params.branch) base.branch = params.branch;
  if (params.gender) base.gender = params.gender;
  if (params.directorate) base.directorate = params.directorate;
  if (params.position) base.position = params.position;

  const merged = [];
  for (let page = 1; page <= maxPages; page += 1) {
    const payload = await fetchUsers({ ...base, page });
    const users = usersArrayFromUsersPayload(payload);
    const pagination = paginationFromUsersPayload(payload);
    merged.push(...users);
    if (!pagination.hasNextPage || users.length === 0) break;
  }
  return merged;
}

/**
 * Get a single user by ID.
 * GET /api/users/:id - returns { data: { user } }.
 */
export const fetchUserById = async (id) => {
  if (!id) return null;
  const response = await apiClient.get(`/api/users/${id}`);
  return response.data?.data?.user ?? null;
};

export const createUser = async (payload) => {
  const response = await apiClient.post('/api/users', payload);
  return response.data;
};

export const updateUser = async ({ id, payload }) => {
  const response = await apiClient.patch(`/api/users/${id}`, payload);
  return response.data;
};

export const deleteUser = async (id) => {
  const response = await apiClient.delete(`/api/users/${id}`);
  return response.data;
};

export const resetUserPassword = async (id) => {
  const response = await apiClient.post(`/api/users/${id}/reset-password`);
  return response.data;
};

export const updateUserProfile = async ({ userId, payload }) => {
  const response = await apiClient.patch(`/api/user-profiles/${userId}`, payload);
  return response.data;
};

export const bulkUploadUsers = async ({ rows, updateExisting = true, dryRun = false } = {}) => {
  const response = await apiClient.post('/api/users/bulk-upload', {
    rows,
    updateExisting,
    dryRun,
  });
  return response.data;
};

export default fetchUsers;
